"use client";

import Image from "next/image";
import { ArrowRight, Info } from "lucide-react";
import { handleImageError, type CPAOffer } from "@/lib/cpa-offers";

interface CPAOfferCardProps {
  offer: CPAOffer;
  onClick?: () => void;
  ctaLabel: string;
  mobileCTALabel: string;
  showInfoIcon?: boolean;
  showPromoCode?: boolean;
  bare?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function CPAOfferCard({
  offer,
  onClick,
  ctaLabel,
  mobileCTALabel,
  showInfoIcon = false,
  showPromoCode = false,
  bare = false,
  className = "",
  "data-testid": dataTestId,
}: CPAOfferCardProps) {
  const { brandColor } = offer;
  const defaultBg = `linear-gradient(to right, ${brandColor}1A, ${brandColor}0D, transparent)`;

  return (
    <a
      href={offer.link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${offer.name} signup offer, opens in new tab`}
      onClick={onClick}
      data-testid={dataTestId}
      className={`group block transition-all duration-200 ${
        bare ? "" : "rounded-xl border p-3"
      } ${className}`}
      style={
        bare
          ? undefined
          : {
              borderColor: `${brandColor}59`,
              background: defaultBg,
            }
      }
      onMouseEnter={
        bare
          ? undefined
          : (e) => {
              e.currentTarget.style.borderColor = `${brandColor}99`;
              e.currentTarget.style.background = `${brandColor}1A`;
            }
      }
      onMouseLeave={
        bare
          ? undefined
          : (e) => {
              e.currentTarget.style.borderColor = `${brandColor}59`;
              e.currentTarget.style.background = defaultBg;
            }
      }
    >
      <div className="flex items-center gap-3">
        <div
          className="flex size-10 shrink-0 items-center justify-center rounded-lg border"
          style={{
            borderColor: `${brandColor}66`,
            backgroundColor: `${brandColor}26`,
          }}
        >
          <Image
            src={offer.logoSrc}
            alt={offer.name}
            width={28}
            height={28}
            className="object-contain"
            onError={handleImageError}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: brandColor }}
          >
            {offer.name}
          </p>
          {showInfoIcon ? (
            <div className="mt-1 flex items-start gap-1.5">
              <Info
                className="mt-0.5 size-3.5 shrink-0"
                style={{ color: brandColor }}
              />
              <p className="text-xs font-semibold leading-snug text-foreground sm:text-[13px]">
                {offer.headline}
              </p>
            </div>
          ) : (
            <p className="mt-0.5 text-xs font-semibold leading-snug text-foreground sm:text-[13px]">
              {offer.headline}
            </p>
          )}
          <p
            className={`${showInfoIcon ? "mt-1" : "mt-0.5"} text-xs leading-snug text-foreground/90 ${showInfoIcon ? "sm:text-[13px]" : ""}`}
          >
            {offer.details}
          </p>
          {showPromoCode && offer.promoCode && (
            <p className="mt-1 text-xs text-muted-foreground">
              Use promo code{" "}
              <span className="font-mono font-semibold text-foreground">
                {offer.promoCode}
              </span>{" "}
              when you sign up.
            </p>
          )}
        </div>
        <div
          className="hidden shrink-0 items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-semibold sm:inline-flex"
          style={{
            borderColor: `${brandColor}80`,
            color: brandColor,
          }}
        >
          {ctaLabel}
          <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
      <div
        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold sm:hidden"
        style={{ color: brandColor }}
      >
        {mobileCTALabel}
        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
      </div>
    </a>
  );
}
